import { formatMonthYear } from './format';

/**
 * The neutral fact about the counterparty that the chat shows instead of their
 * country (the owner asked for the country to be gone from every chat surface).
 *
 * It is public identity data only, the same kind of thing the marketplace
 * already prints about a seller: the seller rank badge (web/assets/js/components
 * /sellerRankBadge.js) and "Member since" (web/shop.html).
 *
 * Honesty rule: this returns null when nothing real is known, and the callers
 * hide the element rather than printing "unknown", a dash or an empty value.
 *
 * Preference order:
 *   1. the seller rank, when the public profile reports a rank we can name
 *   2. how long the account has been a member, from its createdAt
 */
const RANK_NAMES = {
  ROOKIE: 'Rookie',
  WARRIOR: 'Warrior',
  ELITE: 'Elite',
  Z2_MASTER: 'Z2 Master',
  Z2_CROWN: 'Z2 Crown',
  Z2_LEGEND: 'Z2 Legend',
};

/** "z2-master" / "z2 master" / "Z2_MASTER" all normalise to the same key. */
function normalizeRank(rank) {
  if (typeof rank !== 'string') return '';
  return rank.trim().toUpperCase().replace(/[\s-]+/g, '_');
}

/**
 * @returns {{id: string, label: string, value: string, compact: string}|null}
 *   `label`/`value` are the details-panel row; `compact` is the header line.
 */
export function peerFact(profile) {
  if (!profile) return null;

  // An unrecognised rank is not guessed at - we fall through to member-since
  // rather than inventing a label for a value we do not know.
  const rank = RANK_NAMES[normalizeRank(profile.sellerRank)];
  if (rank) {
    return { id: 'sellerLevel', label: 'Seller Level', value: rank, compact: `${rank} Seller` };
  }

  const since = profile.createdAt ? formatMonthYear(profile.createdAt) : '';
  if (since) {
    // "Joined Mar 2024" rather than "Member since Mar 2024": the header status
    // line is ~315px in the three-column desktop layout and ~155px on a phone,
    // and a longer fact squeezed the @handle into an ellipsis. The details panel
    // carries the spelled-out label ("Member Since").
    return { id: 'memberSince', label: 'Member Since', value: since, compact: `Joined ${since}` };
  }

  return null;
}
